import { FC, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { toast } from "sonner";
import $api from "../../shared/api/axios";
import { Tabs, TabsContent, TabsList, TabsTrigger, Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectSeparator, SelectTrigger, SelectValue, Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@libs/zapi-ui/shadcn";
import { AppButton, AppSwitch, FigmaTypography } from "@libs/zapi-ui/custom";
import { ProjectListSheet, TProjectsTarget } from "./ProjectListSheet";

// TODO: remove number[] once all snippets branches use "any"/"none"
type VisibleToDefault = "any" | "none" | number[];

type TOrg = {
  id: number;
  name: string;
  status: string;
};

type TTemplateWithVisibility = {
  slug: string;
  name: string;
  description?: string;
  visibleToDefault: VisibleToDefault;
  overrides: { organizationId: number; enabled: boolean }[];
};

type TTemplateOrgState = {
  slug: string;
  name: string;
  description?: string;
  visibleToDefault: VisibleToDefault;
  override: { enabled: boolean } | null;
  activeProjects: number;
};

type TTemplateOrgProjectCount = {
  organizationId: number;
  activeProjects: number;
};

function defaultVisible(visibleToDefault: VisibleToDefault): boolean {
  return visibleToDefault === "any";
}

function effectivelyVisible(state: TTemplateOrgState): boolean {
  if (state.override !== null) return state.override.enabled;
  return defaultVisible(state.visibleToDefault);
}

function visibleToLabel(visibleToDefault: VisibleToDefault): string {
  if (visibleToDefault === "any") return "All orgs";
  return "No orgs";
}

export const TemplatesPage: FC = () => {
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();

  const tab = searchParams.get("tab") ?? "by-org";
  const selectedOrgId = searchParams.get("orgId") ? parseInt(searchParams.get("orgId") ?? "") : null;
  const selectedSlug = searchParams.get("slug") ?? null;

  const setTab = (value: string) => setSearchParams((p) => { p.set("tab", value); return p; });
  const setOrgId = (id: number) => setSearchParams((p) => { p.set("tab", "by-org"); p.set("orgId", id.toString()); return p; });
  const setSlug = (slug: string) => setSearchParams((p) => { p.set("tab", "by-template"); p.set("slug", slug); return p; });

  const { data: orgs = [] } = useQuery<TOrg[]>({
    queryKey: ["organizations-with-users"],
    queryFn: async () => {
      const response = await $api.get("/organizations/with-users");
      const all: TOrg[] = response?.data?.data || [];
      return all.filter((o) => o.status !== "archived");
    }
  });

  const { data: allTemplates = [], isLoading: templatesLoading } = useQuery<TTemplateWithVisibility[]>({
    queryKey: ["admin-templates"],
    queryFn: async () => {
      const response = await $api.get("/admin/templates");
      return (response?.data || []).sort((a: TTemplateWithVisibility, b: TTemplateWithVisibility) =>
        a.name.localeCompare(b.name)
      );
    }
  });

  const { data: orgTemplates = [], isLoading: orgTemplatesLoading } = useQuery<TTemplateOrgState[]>({
    queryKey: ["admin-templates-by-org", selectedOrgId],
    queryFn: async () => {
      const response = await $api.get(`/admin/templates/by-org/${selectedOrgId}`);
      return response?.data || [];
    },
    enabled: selectedOrgId !== null
  });

  const { data: templateProjectCounts = [], isLoading: templateProjectCountsLoading } = useQuery<TTemplateOrgProjectCount[]>({
    queryKey: ["admin-templates-by-template", selectedSlug],
    queryFn: async () => {
      const response = await $api.get("/admin/templates/by-template", { params: { slug: selectedSlug } });
      return response?.data || [];
    },
    enabled: selectedSlug !== null
  });

  const projectCountByOrgId = useMemo(
    () => new Map(templateProjectCounts.map((c) => [c.organizationId, c.activeProjects])),
    [templateProjectCounts]
  );

  const setOverrideMutation = useMutation({
    mutationFn: ({ slug, orgId, enabled }: { slug: string; orgId: number; enabled: boolean }) =>
      $api.put(`/admin/templates/orgs/${orgId}/${slug}`, { enabled }),
    onSuccess: (_data, variables) => {
      toast.success("Visibility updated");
      queryClient.invalidateQueries({ queryKey: ["admin-templates"] });
      queryClient.invalidateQueries({ queryKey: ["admin-templates-by-org", variables.orgId] });
    },
    onError: (error: AxiosError<{ message?: string }>) => toast.error(error?.response?.data?.message ?? "Failed to update visibility", { icon: "🔴" })
  });

  const removeOverrideMutation = useMutation({
    mutationFn: ({ slug, orgId }: { slug: string; orgId: number }) =>
      $api.delete(`/admin/templates/orgs/${orgId}/${slug}`),
    onSuccess: (_data, variables) => {
      toast.success("Override removed");
      queryClient.invalidateQueries({ queryKey: ["admin-templates"] });
      queryClient.invalidateQueries({ queryKey: ["admin-templates-by-org", variables.orgId] });
    },
    onError: () => toast.error("Failed to remove override")
  });


  const [showAllTemplates, setShowAllTemplates] = useState(false);
  const [projectsTarget, setProjectsTarget] = useState<TProjectsTarget | null>(null);

  const isMutating = setOverrideMutation.isPending || removeOverrideMutation.isPending;
  const selectedTemplate = selectedSlug ? allTemplates.find((t) => t.slug === selectedSlug) ?? null : null;
  const selectedOrgName = selectedOrgId !== null ? orgs.find((o) => o.id === selectedOrgId)?.name ?? "" : "";

  const activeTemplates = allTemplates.filter((t) => t.visibleToDefault === "any" || t.overrides.some((o) => o.enabled));

  const sortedOrgs = useMemo(() => {
    if (!selectedTemplate) return orgs;
    const visibilityMap = new Map<number, boolean>();
    for (const org of orgs) {
      const override = selectedTemplate.overrides.find((o) => o.organizationId === org.id) ?? null;
      visibilityMap.set(org.id, override !== null ? override.enabled : defaultVisible(selectedTemplate.visibleToDefault));
    }
    return [...orgs].sort((a, b) => Number(visibilityMap.get(b.id)) - Number(visibilityMap.get(a.id)));
  }, [orgs, selectedTemplate]);

  return (
    <div className="container mx-auto py-8 max-w-4xl">
      <FigmaTypography component="h1" variant="heading-1" className="mb-6">Template Visibility</FigmaTypography>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="mb-6">
          <TabsTrigger value="by-org">By Org</TabsTrigger>
          <TabsTrigger value="by-template">By Template</TabsTrigger>
        </TabsList>

        <TabsContent value="by-org">
          <div className="mb-6 max-w-xs">
            <label className="block text-[12px] leading-[18px] font-semibold uppercase tracking-wider text-gray-500 mb-1.5">Organization</label>
            <Select
              value={selectedOrgId?.toString() ?? ""}
              onValueChange={(v) => setOrgId(parseInt(v))}
            >
              <SelectTrigger className="h-11 text-base font-medium border-2">
                <SelectValue placeholder="Select an organization" />
              </SelectTrigger>
              <SelectContent>
                {orgs.map((org) => (
                  <SelectItem key={org.id} value={org.id.toString()}>
                    {org.name} (#{org.id})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {selectedOrgId !== null && !orgTemplatesLoading && (() => {
            const count = orgTemplates.filter((t) => effectivelyVisible(t)).length;
            return (
              <div className="mb-3 text-[14px] text-gray-500">
                Visible templates: <span className="font-medium">{count} {count === 1 ? "template" : "templates"}</span>
              </div>
            );
          })()}

          {selectedOrgId !== null && (
            <div className="rounded-lg border bg-card shadow-sm">
              {orgTemplatesLoading ? (
                <div className="p-8 text-center text-gray-500">Loading...</div>
              ) : (
                <table className="w-full">
                  <thead>
                    <tr className="border-b bg-gray-50">
                      <th className="px-6 py-4 text-left text-[14px] font-medium text-gray-900">Template</th>
                      <th className="px-6 py-4 text-left text-[14px] font-medium text-gray-900">Template default</th>
                      <th className="px-6 py-4 text-left text-[14px] font-medium text-gray-900">Override</th>
                      <th className="px-6 py-4 text-left text-[14px] font-medium text-gray-900">Projects</th>
                      <th className="px-6 py-4 text-left text-[14px] font-medium text-gray-900">Visible</th>
                      <th className="px-6 py-4 text-left text-[14px] font-medium text-gray-900"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {[...orgTemplates].sort((a, b) => {
                      const visibleDiff = Number(effectivelyVisible(b)) - Number(effectivelyVisible(a));
                      return visibleDiff !== 0 ? visibleDiff : a.name.localeCompare(b.name);
                    }).map((t) => {
                      const visible = effectivelyVisible(t);
                      return (
                        <tr key={t.slug} className="border-b hover:bg-gray-50">
                          <td className="px-6 py-4">
                            <button
                              className="text-[14px] font-medium text-blue-600 hover:underline text-left"
                              onClick={() => setSlug(t.slug)}
                            >
                              {t.name}
                            </button>
                            <FigmaTypography component="div" variant="body-3" className="text-gray-400">{t.slug}</FigmaTypography>
                          </td>
                          <td className="px-6 py-4 text-[14px] text-gray-600">{visibleToLabel(t.visibleToDefault)}</td>
                          <td className="px-6 py-4 text-[14px] text-gray-600">
                            {t.override !== null ? (
                              <span className={`inline-flex items-center px-2 py-1 rounded-full text-[12px] leading-[18px] font-medium ${t.override.enabled ? "bg-blue-100 text-blue-800" : "bg-orange-100 text-orange-800"}`}>
                                {t.override.enabled ? "Forced on" : "Forced off"}
                              </span>
                            ) : (
                              <span className="text-gray-400">—</span>
                            )}
                          </td>
                          <td className="px-6 py-4 text-[14px] text-gray-600">
                            {t.activeProjects > 0 ? (
                              <button
                                className="font-medium text-blue-600 hover:underline"
                                onClick={() =>
                                  setProjectsTarget({ orgId: selectedOrgId, slug: t.slug, templateName: t.name, orgName: selectedOrgName })
                                }
                              >
                                {t.activeProjects}
                              </button>
                            ) : (
                              <span className="text-gray-400">—</span>
                            )}
                          </td>
                          <td className="px-6 py-4">
                            <AppSwitch
                              checked={visible}
                              onCheckedChange={(checked) =>
                                setOverrideMutation.mutate({ slug: t.slug, orgId: selectedOrgId, enabled: checked })
                              }
                              disabled={isMutating}
                            />
                          </td>
                          <td className="px-6 py-4">
                            {t.override !== null && (
                              <AppButton
                                size="sm"
                                appearance="text"
                                className="text-[12px] leading-[18px] text-gray-500"
                                disabled={isMutating}
                                onClick={() => removeOverrideMutation.mutate({ slug: t.slug, orgId: selectedOrgId })}
                              >
                                Reset
                              </AppButton>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
          )}
        </TabsContent>

        <TabsContent value="by-template">
          <div className="mb-2 flex items-end gap-4">
          <div className="max-w-sm w-full">
            <div className="flex items-center justify-between mb-1.5">
              <label className="block text-[12px] leading-[18px] font-semibold uppercase tracking-wider text-gray-500">Template</label>
              <TooltipProvider delayDuration={200}>
                <div className="bg-muted text-muted-foreground inline-flex h-9 w-fit items-center justify-center rounded-lg p-1">
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <button
                        className={`inline-flex items-center justify-center rounded-md px-2 py-1 text-[14px] font-medium whitespace-nowrap transition-[color,box-shadow] ${!showAllTemplates ? "bg-background text-foreground shadow-sm" : ""}`}
                        onClick={() => setShowAllTemplates(false)}
                      >
                        Active ({activeTemplates.length})
                      </button>
                    </TooltipTrigger>
                    <TooltipContent side="bottom" className="text-[12px] leading-[18px] max-w-[220px]">
                      <p>Templates visible to at least one org</p>
                    </TooltipContent>
                  </Tooltip>
                  <button
                    className={`inline-flex items-center justify-center rounded-md px-2 py-1 text-[14px] font-medium whitespace-nowrap transition-[color,box-shadow] ${showAllTemplates ? "bg-background text-foreground shadow-sm" : ""}`}
                    onClick={() => setShowAllTemplates(true)}
                  >
                    All ({allTemplates.length})
                  </button>
                </div>
              </TooltipProvider>
            </div>
            <Select
              value={selectedSlug ?? ""}
              onValueChange={(v) => setSlug(v)}
            >
              <SelectTrigger className="h-11 text-base font-medium border-2">
                <SelectValue placeholder="Select a template">
                  {selectedSlug ? (allTemplates.find((t) => t.slug === selectedSlug)?.name ?? selectedSlug) : null}
                </SelectValue>
              </SelectTrigger>
              <SelectContent>
                {showAllTemplates ? (
                  <>
                    <SelectGroup>
                      <SelectLabel>Active</SelectLabel>
                      {activeTemplates.map((t) => (
                        <SelectItem key={t.slug} value={t.slug} textValue={t.name}>
                          <div className="py-0.5">
                            <div>{t.name}</div>
                            <FigmaTypography component="div" variant="body-3" className="text-gray-400">{t.slug}</FigmaTypography>
                          </div>
                        </SelectItem>
                      ))}
                    </SelectGroup>
                    <SelectSeparator />
                    <SelectGroup>
                      <SelectLabel>Zero visibility</SelectLabel>
                      {allTemplates.filter((t) => !activeTemplates.includes(t)).map((t) => (
                        <SelectItem key={t.slug} value={t.slug} textValue={t.name} className="opacity-50">
                          <div className="py-0.5">
                            <div>{t.name}</div>
                            <FigmaTypography component="div" variant="body-3" className="text-gray-400">{t.slug}</FigmaTypography>
                          </div>
                        </SelectItem>
                      ))}
                    </SelectGroup>
                  </>
                ) : (
                  activeTemplates.map((t) => (
                    <SelectItem key={t.slug} value={t.slug} textValue={t.name}>
                      <div className="py-0.5">
                        <div>{t.name}</div>
                        <FigmaTypography component="div" variant="body-3" className="text-gray-400">{t.slug}</FigmaTypography>
                      </div>
                    </SelectItem>
                  ))
                )}
              </SelectContent>
            </Select>
          </div>
            {selectedSlug && (
              <span className="text-[14px] text-gray-400 pb-2.5">{selectedSlug}</span>
            )}
          </div>

          {selectedTemplate !== null && (
            <>
              {selectedTemplate.description && (
                <div className="mt-1 mb-4 text-[14px] text-gray-500">{selectedTemplate.description}</div>
              )}
              <div className="mb-3 text-[14px] text-gray-500">
                {(() => {
                  const isAny = selectedTemplate.visibleToDefault === "any";
                  const enabledOverrides = selectedTemplate.overrides.filter((o) => o.enabled).length;
                  const disabledOverrides = selectedTemplate.overrides.filter((o) => !o.enabled).length;
                  if (isAny) {
                    const count = orgs.length - disabledOverrides;
                    return <>Visible to: <span className="font-medium">{count} {count === 1 ? "org" : "orgs"}</span></>;
                  }
                  return <>Visible to: <span className="font-medium">{enabledOverrides} {enabledOverrides === 1 ? "org" : "orgs"}</span></>;
                })()}
              </div>
              <div className="rounded-lg border bg-card shadow-sm">
                {templatesLoading ? (
                  <div className="p-8 text-center text-gray-500">Loading...</div>
                ) : (
                  <table className="w-full">
                    <thead>
                      <tr className="border-b bg-gray-50">
                        <th className="px-6 py-4 text-left text-[14px] font-medium text-gray-900">Organization</th>
                        <th className="px-6 py-4 text-left text-[14px] font-medium text-gray-900">Override</th>
                        <th className="px-6 py-4 text-left text-[14px] font-medium text-gray-900">Projects</th>
                        <th className="px-6 py-4 text-left text-[14px] font-medium text-gray-900">Visible</th>
                        <th className="px-6 py-4 text-left text-[14px] font-medium text-gray-900"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {sortedOrgs.map((org) => {
                        const override = selectedTemplate.overrides.find((o) => o.organizationId === org.id) ?? null;
                        const visible = override !== null
                          ? override.enabled
                          : defaultVisible(selectedTemplate.visibleToDefault);
                        return (
                          <tr key={org.id} className="border-b hover:bg-gray-50">
                            <td className="px-6 py-4 text-[14px] font-medium">
                              <button
                                className="text-blue-600 hover:underline"
                                onClick={() => setOrgId(org.id)}
                              >
                                {org.name}
                              </button>
                              <span className="text-gray-400 font-normal ml-1">(#{org.id})</span>
                            </td>
                            <td className="px-6 py-4 text-[14px] text-gray-600">
                              {override !== null ? (
                                <span className={`inline-flex items-center px-2 py-1 rounded-full text-[12px] leading-[18px] font-medium ${override.enabled ? "bg-blue-100 text-blue-800" : "bg-orange-100 text-orange-800"}`}>
                                  {override.enabled ? "Forced on" : "Forced off"}
                                </span>
                              ) : (
                                <span className="text-gray-400">—</span>
                              )}
                            </td>
                            <td className="px-6 py-4 text-[14px] text-gray-600">
                              {templateProjectCountsLoading ? (
                                <span className="text-gray-300">…</span>
                              ) : (projectCountByOrgId.get(org.id) ?? 0) > 0 ? (
                                <button
                                  className="font-medium text-blue-600 hover:underline"
                                  onClick={() =>
                                    setProjectsTarget({ orgId: org.id, slug: selectedTemplate.slug, templateName: selectedTemplate.name, orgName: org.name })
                                  }
                                >
                                  {projectCountByOrgId.get(org.id)}
                                </button>
                              ) : (
                                <span className="text-gray-400">—</span>
                              )}
                            </td>
                            <td className="px-6 py-4">
                              <AppSwitch
                                checked={visible}
                                onCheckedChange={(checked) =>
                                  setOverrideMutation.mutate({ slug: selectedTemplate.slug, orgId: org.id, enabled: checked })
                                }
                                disabled={isMutating}
                              />
                            </td>
                            <td className="px-6 py-4">
                              {override !== null && (
                                <AppButton
                                  size="sm"
                                  appearance="text"
                                  className="text-[12px] leading-[18px] text-gray-500"
                                  disabled={isMutating}
                                  onClick={() => removeOverrideMutation.mutate({ slug: selectedTemplate.slug, orgId: org.id })}
                                >
                                  Reset
                                </AppButton>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                )}
              </div>
            </>
          )}
        </TabsContent>
      </Tabs>

      <ProjectListSheet
        target={projectsTarget}
        onOpenChange={(open) => {
          if (!open) setProjectsTarget(null);
        }}
      />
    </div>
  );
};
